import { Reducer } from 'redux'
import {
  addCollection,
  clearSelectedCollection,
  closeCreateCollection,
  openCreateCollection,
  renameCollection,
  resetTeamRelationship,
  selectCollection,
  setCollectionArchived,
  setCollections,
  setMembers,
  setCreationStep,
  setDraftCollectionName,
  setDraftCollectionType,
  setDraftSelectedMemberIds,
  toggleDraftMember,
  updateCollectionMemberIds,
  TeamRelationshipActionTypes,
} from './action'
import { normalizeCollections, normalizeMembers } from './dummyData'
import { CollectionDraft, TeamRelationshipState } from './types'

const emptyDraft: CollectionDraft = {
  type: null,
  name: '',
  selectedMemberIds: [],
}

export const initialState: TeamRelationshipState = {
  collectionsById: {},
  collectionIds: [],
  membersById: {},
  memberIds: [],
  selectedCollectionId: null,
  creationStep: 'closed',
  draft: emptyDraft,
}

type TeamRelationshipAction =
  | ReturnType<typeof openCreateCollection>
  | ReturnType<typeof closeCreateCollection>
  | ReturnType<typeof setCreationStep>
  | ReturnType<typeof setDraftCollectionType>
  | ReturnType<typeof setDraftCollectionName>
  | ReturnType<typeof setDraftSelectedMemberIds>
  | ReturnType<typeof toggleDraftMember>
  | ReturnType<typeof addCollection>
  | ReturnType<typeof setCollections>
  | ReturnType<typeof setMembers>
  | ReturnType<typeof selectCollection>
  | ReturnType<typeof clearSelectedCollection>
  | ReturnType<typeof renameCollection>
  | ReturnType<typeof updateCollectionMemberIds>
  | ReturnType<typeof setCollectionArchived>
  | ReturnType<typeof resetTeamRelationship>

export const reducer: Reducer<TeamRelationshipState, TeamRelationshipAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case TeamRelationshipActionTypes.OPEN_CREATE_COLLECTION:
      return {
        ...state,
        creationStep: 'selectType',
        draft: emptyDraft,
      }
    case TeamRelationshipActionTypes.CLOSE_CREATE_COLLECTION:
      return {
        ...state,
        creationStep: 'closed',
        draft: emptyDraft,
      }
    case TeamRelationshipActionTypes.SET_CREATION_STEP:
      return {
        ...state,
        creationStep: action.payload,
      }
    case TeamRelationshipActionTypes.SET_DRAFT_COLLECTION_TYPE:
      return {
        ...state,
        draft: {
          ...state.draft,
          type: action.payload,
        },
      }
    case TeamRelationshipActionTypes.SET_DRAFT_COLLECTION_NAME:
      return {
        ...state,
        draft: {
          ...state.draft,
          name: action.payload,
        },
      }
    case TeamRelationshipActionTypes.SET_DRAFT_SELECTED_MEMBER_IDS:
      return {
        ...state,
        draft: {
          ...state.draft,
          selectedMemberIds: action.payload,
        },
      }
    case TeamRelationshipActionTypes.TOGGLE_DRAFT_MEMBER: {
      const memberId = action.payload
      const selected = state.draft.selectedMemberIds
      const selectedMemberIds = selected.includes(memberId)
        ? selected.filter(id => id !== memberId)
        : [...selected, memberId]
      return {
        ...state,
        draft: {
          ...state.draft,
          selectedMemberIds,
        },
      }
    }
    case TeamRelationshipActionTypes.ADD_COLLECTION: {
      const collection = action.payload
      if (state.collectionsById[collection.id]) {
        return state
      }
      return {
        ...state,
        collectionsById: {
          ...state.collectionsById,
          [collection.id]: collection,
        },
        collectionIds: [...state.collectionIds, collection.id],
      }
    }
    case TeamRelationshipActionTypes.SET_COLLECTIONS:
      return {
        ...state,
        ...normalizeCollections(action.payload),
        selectedCollectionId:
          state.selectedCollectionId &&
          action.payload.some(
            collection => collection.id === state.selectedCollectionId
          )
            ? state.selectedCollectionId
            : null,
      }
    case TeamRelationshipActionTypes.SET_MEMBERS:
      return {
        ...state,
        ...normalizeMembers(action.payload),
      }
    case TeamRelationshipActionTypes.SELECT_COLLECTION:
      return {
        ...state,
        selectedCollectionId: state.collectionsById[action.payload]
          ? action.payload
          : state.selectedCollectionId,
      }
    case TeamRelationshipActionTypes.CLEAR_SELECTED_COLLECTION:
      return {
        ...state,
        selectedCollectionId: null,
      }
    case TeamRelationshipActionTypes.RENAME_COLLECTION: {
      const { collectionId, name } = action.payload
      const trimmedName = name.trim()
      const collection = state.collectionsById[collectionId]
      if (!collection || !trimmedName) {
        return state
      }
      return {
        ...state,
        collectionsById: {
          ...state.collectionsById,
          [collectionId]: {
            ...collection,
            name: trimmedName,
          },
        },
      }
    }
    case TeamRelationshipActionTypes.UPDATE_COLLECTION_MEMBER_IDS: {
      const { collectionId, memberIds } = action.payload
      const collection = state.collectionsById[collectionId]
      if (!collection) {
        return state
      }
      return {
        ...state,
        collectionsById: {
          ...state.collectionsById,
          [collectionId]: {
            ...collection,
            memberIds: [...memberIds],
          },
        },
      }
    }
    case TeamRelationshipActionTypes.SET_COLLECTION_ARCHIVED: {
      const { collectionId, isArchived } = action.payload
      const collection = state.collectionsById[collectionId]
      if (!collection) {
        return state
      }
      return {
        ...state,
        collectionsById: {
          ...state.collectionsById,
          [collectionId]: {
            ...collection,
            isArchived,
          },
        },
      }
    }
    case TeamRelationshipActionTypes.RESET_TEAM_RELATIONSHIP:
      return initialState
    default:
      return state
  }
}
